/**
 * Mixin for form controllers that handle media uploads.
 * Provides shared CSRF token, batch upload, and single-file upload.
 *
 * Usage: export default class extends MediaFormMixin(Controller) { ... }
 */
export const MediaFormMixin = (Base) =>
  class extends Base {
    get csrfToken() {
      return document.querySelector('meta[name="csrf-token"]')?.content || ""
    }

    /**
     * Upload multiple files in a single request (batch upload on form submit).
     * Used by event-form and post-form.
     * @param {File[]} files - Files to upload
     * @param {string} [uploadUrl='/medias'] - Upload endpoint
     * @returns {Promise<number[]>} - Array of uploaded media IDs
     */
    async uploadMediaFiles(files, uploadUrl = "/medias") {
      const formData = new FormData()
      files.forEach((file) => formData.append("media[file][]", file))

      const response = await fetch(uploadUrl, {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.csrfToken,
          Accept: "application/json"
        },
        body: formData,
        credentials: "same-origin"
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error("Upload failed:", errorText)
        throw new Error("Upload failed")
      }

      const data = await response.json()

      if (data.failed && data.failed.length > 0) {
        console.error("Some uploads failed:", data.failed)
      }

      return (data.medias || []).map((m) => m.id)
    }

    /**
     * Upload a single file (immediate upload on file select).
     * Used by testimonial-form.
     * @param {File} file - File to upload
     * @param {string} [uploadUrl='/medias'] - Upload endpoint
     * @returns {Promise<{id: number, original_url: string, thumb_url: string, category: string}|null>}
     */
    async uploadMediaFile(file, uploadUrl = "/medias") {
      const formData = new FormData()
      formData.append("media[file][]", file)

      const response = await fetch(uploadUrl, {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.csrfToken,
          Accept: "application/json"
        },
        body: formData,
        credentials: "same-origin"
      })

      if (!response.ok) throw new Error("Upload failed")

      const data = await response.json()

      if (data.medias && data.medias.length > 0) {
        const media = data.medias[0]
        return {
          id: media.id,
          original_url: media.original_url,
          thumb_url: media.thumb_url || media.original_url,
          category: media.category || "image"
        }
      }

      return null
    }

    get isInFlutterApp() {
      return typeof window.flutter_inappwebview !== "undefined"
    }

    async openWebcam() {
      if (this.isInFlutterApp) {
        const hasPermission = await this.requestCameraPermission()
        if (!hasPermission) return
      }

      const modal = document.getElementById("webcam-modal")
      if (modal) {
        modal.classList.remove("hidden")
        window.dispatchEvent(new CustomEvent("webcam:open"))
      }
    }

    async requestCameraPermission() {
      try {
        const result = await window.flutter_inappwebview.callHandler("requestCameraPermission")
        return result?.granted || result === true
      } catch (error) {
        console.error("Error requesting camera permission:", error)
        return true
      }
    }

    /**
     * Setup webcam capture listener. Controllers must implement handleWebcamCaptured().
     */
    setupWebcamListener() {
      this.boundHandleWebcamCaptured = this.handleWebcamCaptured.bind(this)
      window.addEventListener("webcam:captured", this.boundHandleWebcamCaptured)
    }

    /**
     * Teardown webcam listener. Call from disconnect().
     */
    disconnectWebcamListener() {
      if (this.boundHandleWebcamCaptured) {
        window.removeEventListener("webcam:captured", this.boundHandleWebcamCaptured)
      }
    }

    /**
     * Show/hide media preview container based on item count.
     * Used by post-form and event-form (batch upload).
     */
    updateMediaPreviewVisibility() {
      if (!this.hasMediaPreviewTarget) return

      const hasItems = this.mediaPreviewTarget.querySelectorAll("[data-media-id], [data-file-id]").length > 0
      if (hasItems) {
        this.mediaPreviewTarget.classList.remove("hidden")
      } else {
        this.mediaPreviewTarget.classList.add("hidden")
      }
    }

    /**
     * Revoke object URLs on preview elements. Call from disconnect() for batch-upload forms.
     */
    revokeObjectUrls() {
      if (!this.hasMediaPreviewTarget) return

      this.mediaPreviewTarget.querySelectorAll("[data-object-url]").forEach((el) => {
        if (el.dataset.objectUrl) {
          URL.revokeObjectURL(el.dataset.objectUrl)
        }
      })
    }

    /**
     * Build a local file preview element for batch-upload forms.
     * @param {File} file
     * @param {Object} options - { removeAction, fileId?, fileIndex?, size }
     * @returns {{ element: HTMLElement, objectUrl: string }}
     */
    buildLocalPreviewElement(file, { removeAction, fileId, fileIndex, size = "md" }) {
      const isVideo = file.type.startsWith("video/")
      const objectUrl = URL.createObjectURL(file)
      const sizeClasses = size === "sm" ? "w-24 h-24" : "w-32 h-32"
      const deleteBtnClasses = "absolute top-1 right-1 bg-red-500/80 hover:bg-red-500 text-white rounded-full p-1.5 transition-colors z-10 shadow-md"
      const videoIconPath = "M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z"
      const closeIconPath = "M6 18L18 6M6 6l12 12"

      const preview = document.createElement("div")
      preview.className = `relative group flex-none ${sizeClasses}`
      if (fileId) preview.dataset.fileId = fileId
      if (fileIndex !== undefined) preview.dataset.fileIndex = fileIndex
      preview.dataset.objectUrl = objectUrl

      const buttonAttrs = fileId
        ? `data-action="click->${removeAction}" data-file-id="${fileId}"`
        : `data-action="click->${removeAction}" data-file-index="${fileIndex}"`

      if (isVideo) {
        preview.innerHTML = `
          <video src="${objectUrl}" class="w-full h-full object-cover rounded-lg" muted></video>
          <div class="absolute inset-0 flex items-center justify-center bg-black/50 rounded pointer-events-none">
            <svg class="w-3 h-3 inline" fill="currentColor" viewBox="0 0 20 20"><path d="${videoIconPath}"/></svg>
          </div>
          <button type="button" class="${deleteBtnClasses}" ${buttonAttrs}>
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="${closeIconPath}"/></svg>
          </button>
        `
      } else {
        preview.innerHTML = `
          <img src="${objectUrl}" class="w-full h-full object-cover rounded-lg" alt="">
          <button type="button" class="${deleteBtnClasses}" ${buttonAttrs}>
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="${closeIconPath}"/></svg>
          </button>
        `
      }

      return { element: preview, objectUrl }
    }
  }
